import React from 'react';
import axios from 'axios';
import { Check, ChevronDown, ChevronUp, Clock, Download, Edit3, GitMerge, HelpCircle, Link2, Loader, MapPin, Plus, Save, Search, Settings, Trash2, Upload, Users, X } from 'lucide-react';
import ClearableField from './ClearableField';
import HistoricalDatePicker from './HistoricalDatePicker';
import { BLANK_MEMBER, FATE_CONFIG, RELATION_CONFIG } from '../constants/family';
import { generateGEDCOM } from '../utils/gedcomUtils';
import { membersToChartData, inferRelationshipsFromMembers } from '../utils/familyChartAdapter';
import FamilyChartPane, { GenogramSettingsModal } from './FamilyChartPane';
import GedcomMergeModal from './GedcomMergeModal';
import AutocompleteInput from './AutocompleteInput';
import LoadFailure from './LoadFailure';
import { PlayButton } from './AudioClipPlayer';
import { useToast } from '../toastContext';
import { useConfirm } from '../confirmContext';
import { usePrompt } from '../promptContext';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import Modal from './ui/Modal';
import { Z } from '../constants/tokens';

const _ts = () => { const d = new Date(); return `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}_${String(d.getHours()).padStart(2,'0')}${String(d.getMinutes()).padStart(2,'0')}${String(d.getSeconds()).padStart(2,'0')}`; };

export default function FamilyTree({ root, theme, backendUrl, subjectRecord, onMembersLoaded, currentUser = null }) {
  const { pushToast } = useToast();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [members, setMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedId, setSelectedId] = React.useState(null);
  const [editId, setEditId] = React.useState(null);
  const [draft, setDraft] = React.useState(BLANK_MEMBER);
  const [saving, setSaving] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const gedcomFileRef = React.useRef(null);
  const [leftPanelWidth, setLeftPanelWidth] = React.useState(() => {
    const saved = sessionStorage.getItem('gwv_genogram_left_w');
    return saved ? Number(saved) : 28;
  });
  const isDraggingRef = React.useRef(false);
  const containerRef = React.useRef(null);
  const [relGroupSplit, setRelGroupSplit] = React.useState(() => {
    const saved = sessionStorage.getItem('gwv_genogram_relgroup_pct');
    return saved ? parseFloat(saved) : 60;
  });
  const bottomPanelRef = React.useRef(null);

  const [topPanelPct, setTopPanelPct] = React.useState(() => {
    const saved = sessionStorage.getItem('gwv_genogram_top_pct');
    return saved ? Number(saved) : 55;
  });
  const rightPanelRef = React.useRef(null);
  const [showClearConfirm, setShowClearConfirm] = React.useState(false);
  const [clearing, setClearing] = React.useState(false);
  const [rebuildingRels, setRebuildingRels] = React.useState(false);
  const [runningRelQA, setRunningRelQA] = React.useState(false);
  const [showCardSettings, setShowCardSettings] = React.useState(false);
  const [cardSettingsVersion, setCardSettingsVersion] = React.useState(0);
  const genderColors = React.useMemo(() => {
    try { const s = JSON.parse(localStorage.getItem('gwv_genogram_card_settings') || '{}'); return { M: s.maleColor || '#789fac', F: s.femaleColor || '#c48a92' }; }
    catch { return { M: '#789fac', F: '#c48a92' }; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardSettingsVersion]);
  const [showGenogramHelp, setShowGenogramHelp] = React.useState(false);
  const [mergeGedcomText, setMergeGedcomText] = React.useState(null); // when set, opens merge modal
  const [mergeSession, setMergeSession] = React.useState(null); // existing session to resume
  const [pendingMergeSession, setPendingMergeSession] = React.useState(null); // check if one exists
  const [helpContent, setHelpContent] = React.useState(null);
  React.useEffect(() => {
    if (showGenogramHelp && !helpContent) {
      axios.get(`${backendUrl}/admin/help-menus/genogram-help`)
        .then(r => {
          if (r.data && r.data.items && r.data.items.length > 0) setHelpContent(r.data);
        })
        .catch(() => {});
    }
  }, [showGenogramHelp, helpContent, backendUrl]);
  const jsonRestoreRef = React.useRef(null);
  const isAdmin = currentUser?.role === 'admin';
  const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'editor';

  const rebuildRelationships = () => {
    setRebuildingRels(true);
    axios.post(`${backendUrl}/subject/family/rebuild-relationships?root=${encodeURIComponent(root)}`)
      .then(r => {
        const d = r.data;
        pushToast('family-rebuild-rels', 'success', `Rebuilt relationships: ${d.created} created, ${d.deleted} old inferred deleted`);
        loadStructure();
      })
      .catch(err => pushToast('family-rebuild-rels', 'error', 'Rebuild failed: ' + (err.response?.data?.detail || err.message)))
      .finally(() => setRebuildingRels(false));
  };

  const relQaPollRef = React.useRef(null);
  React.useEffect(() => () => { if (relQaPollRef.current) clearInterval(relQaPollRef.current); }, []);

  const runRelationshipQA = () => {
    setRunningRelQA(true);
    axios.post(`${backendUrl}/subject/family/relationship-qa?root=${encodeURIComponent(root)}`)
      .then(r => {
        const jobId = r.data.job_id;
        let attempts = 0;
        let pollFailures = 0;
        const MAX_ATTEMPTS = 150; // ~5 minutes at 2s intervals
        const stopPoll = () => { if (relQaPollRef.current) { clearInterval(relQaPollRef.current); relQaPollRef.current = null; } };
        // Poll for completion
        const poll = setInterval(() => {
          attempts += 1;
          if (attempts > MAX_ATTEMPTS) {
            stopPoll();
            setRunningRelQA(false);
            pushToast('family-rel-qa', 'error', 'Relationship QA timed out. It may still be running — reload to check results.');
            return;
          }
          axios.get(`${backendUrl}/jobs/${jobId}`).then(jr => {
            pollFailures = 0;
            const job = jr.data;
            if (job.status === 'complete') {
              stopPoll();
              setRunningRelQA(false);
              const res = job.result || {};
              pushToast('family-rel-qa', 'success', `Relationship QA complete: ${res.new_relationships || 0} new, ${res.updated_children || 0} updated, ${res.skipped_existing || 0} skipped`);
              loadStructure();
            } else if (job.status === 'failed') {
              stopPoll();
              setRunningRelQA(false);
              pushToast('family-rel-qa', 'error', 'Relationship QA failed: ' + (job.error || 'Unknown error'));
            }
          }).catch(() => {
            pollFailures += 1;
            if (pollFailures >= 5) {
              stopPoll();
              setRunningRelQA(false);
              pushToast('family-rel-qa', 'error', 'Relationship QA status could not be retrieved. Please reload to check results.');
            }
          });
        }, 2000);
        relQaPollRef.current = poll;
      })
      .catch(err => { setRunningRelQA(false); pushToast('family-rel-qa', 'error', 'Failed to start: ' + (err.response?.data?.detail || err.message)); });
  };

  const onDividerPointerDown = React.useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!isDraggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      const clamped = Math.min(45, Math.max(15, pct));
      setLeftPanelWidth(clamped);
    };
    const onUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      setLeftPanelWidth(w => { sessionStorage.setItem('gwv_genogram_left_w', String(w)); return w; });
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, []);

  const onVDividerPointerDown = React.useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!isDraggingRef.current || !rightPanelRef.current) return;
      const rect = rightPanelRef.current.getBoundingClientRect();
      const pct = ((ev.clientY - rect.top) / rect.height) * 100;
      setTopPanelPct(Math.min(80, Math.max(25, pct)));
    };
    const onUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      setTopPanelPct(w => { sessionStorage.setItem('gwv_genogram_top_pct', String(w)); return w; });
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, []);

  const onRelGroupDividerDown = React.useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!isDraggingRef.current || !bottomPanelRef.current) return;
      const rect = bottomPanelRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setRelGroupSplit(Math.min(85, Math.max(25, pct)));
    };
    const onUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      setRelGroupSplit(w => { sessionStorage.setItem('gwv_genogram_relgroup_pct', String(w)); return w; });
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, []);

  const importGEDCOM = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      if (members.length > 0) {
        // Existing members — open merge screen
        setMergeGedcomText(text);
      } else {
        // Empty genogram — direct import (no merge needed)
        setImporting(true);
        const tok = sessionStorage.getItem('gwv_token');
        axios.post(`${backendUrl}/subject/family/import-gedcom?root=${encodeURIComponent(root)}`,
          { gedcom: text },
          tok ? { headers: { Authorization: `Bearer ${tok}` } } : {}
        )
          .then(res => {
            pushToast('gedcom-import', 'success', `GEDCOM imported: ${res.data.members_inserted} members added, ${res.data.relationships_inserted} relationships created.`);
            load();
            loadStructure();
          })
          .catch(err => pushToast('gedcom-import', 'error', `Import failed: ${err?.response?.data?.detail || err.message}`))
          .finally(() => setImporting(false));
      }
    };
    reader.readAsText(file);
  };
  const [activePanel, setActivePanel] = React.useState('people');
  const [relations, setRelations] = React.useState([]);
  const [groups, setGroups] = React.useState([]);
  const [dragMemberId, setDragMemberId] = React.useState(null);
  const subjectRef = React.useMemo(
    () => { const s = members.find(m => m.relation === 'subject'); return s ? String(s.id) : 'subject'; },
    [members]
  );
  const [relationshipDraft, setRelationshipDraft] = React.useState({
    partnerA: 'subject',
    partnerB: '',
    children: [],
    label: '',
  });
  // Update draft default when subjectRef becomes known (after members load)
  React.useEffect(() => {
    setRelationshipDraft(prev => prev.partnerB === '' && prev.children.length === 0 ? { ...prev, partnerA: subjectRef } : prev);
  }, [subjectRef]);
  const [editingRelId, setEditingRelId] = React.useState(null);  // ID of relationship being edited
  const [groupsOpen, setGroupsOpen] = React.useState(false);
  const [groupName, setGroupName] = React.useState('');
  const structureReadyRef = React.useRef(false);
  const hydratingStructureRef = React.useRef(false);
  // True when the structure GET failed. Distinct from "loaded and empty" —
  // conflating the two is what let a dropped request overwrite the genogram.
  const [structureError, setStructureError] = React.useState(false);
  const peopleListRef = React.useRef(null);
  const peopleScrollTopRef = React.useRef(0);

  const subjectName = subjectRecord?.display_name || root.replace(/_/g, ' ').trim();

  const load = React.useCallback(() => {
    setLoading(true);
    axios.get(`${backendUrl}/subject/family?root=${encodeURIComponent(root)}`)
      .then(r => {
        setMembers(r.data || []);
        if (onMembersLoaded) onMembersLoaded((r.data || []).length);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [backendUrl, onMembersLoaded, root]);

  React.useEffect(() => { load(); }, [load]);

  // Check for pending merge session
  React.useEffect(() => {
    axios.get(`${backendUrl}/subject/family/merge-session?root=${encodeURIComponent(root)}`)
      .then(r => { if (r.data.session) setPendingMergeSession(r.data.session); })
      .catch(() => {});
  }, [backendUrl, root]);

  const normalizeStructure = React.useCallback((data) => ({
    relationships: Array.isArray(data?.relationships) ? data.relationships.map((rel, idx) => ({
      id: rel.id,
      partnerA: String(rel.partnerA || 'subject'),
      partnerB: String(rel.partnerB || ''),
      children: Array.isArray(rel.children) ? rel.children.map(String) : [],
      label: rel.label || '',
      sortOrder: rel.sortOrder ?? idx,
      // Fall back to the legacy id prefix for rows written before `source`
      // was persisted, so existing guesses are still identifiable.
      source: rel.source || (String(rel.id).startsWith('inferred-') ? 'inferred' : 'manual'),
    })) : [],
    groups: Array.isArray(data?.groups) ? data.groups.map((group, idx) => ({
      id: group.id,
      name: group.name || '',
      memberIds: Array.isArray(group.memberIds) ? group.memberIds.map(String) : [],
      sortOrder: group.sortOrder ?? idx,
      color: group.color || '',
    })) : [],
  }), []);

  const loadStructure = React.useCallback(() => {
    hydratingStructureRef.current = true;
    axios.get(`${backendUrl}/subject/family/structure?root=${encodeURIComponent(root)}`)
      .then(r => {
        const normalized = normalizeStructure(r.data);
        setRelations(normalized.relationships);
        setGroups(normalized.groups);
        setStructureError(false);
        // Only a successful load marks the structure ready. See the catch.
        structureReadyRef.current = true;
      })
      .catch(() => {
        // Deliberately do NOT clear relations/groups, and do NOT mark the
        // structure ready.
        //
        // This used to empty both and then set structureReadyRef in .finally,
        // which meant a failed GET looked exactly like a genogram that has
        // never been built. Both the inference effect and the auto-save effect
        // below gate on structureReadyRef, so the failure unlocked them:
        // inference saw relations.length === 0, generated guesses, and the save
        // effect PUT those guesses — along with the now-empty groups — over the
        // real stored structure. A dropped request destroyed data.
        //
        // Leaving the ref false is what holds both effects shut.
        setStructureError(true);
      })
      .finally(() => {
        hydratingStructureRef.current = false;
      });
  }, [backendUrl, normalizeStructure, root]);

  // ── Auto-infer relationships when members exist but no relationships are stored ──
  // Runs once after both members and structure have loaded.
  // Sets relations state → triggers the existing auto-save effect → persisted to DB.
  const inferredRef = React.useRef(false);
  React.useEffect(() => {
    if (!structureReadyRef.current || hydratingStructureRef.current) return;
    if (inferredRef.current) return; // only infer once per mount
    if (members.length === 0) return; // no members yet
    // Infer ONLY into an empty genogram. Anything already stored is
    // authoritative, including relationships that were inferred earlier and
    // then corrected by a human.
    //
    // This previously skipped only when a relationship id did NOT start with
    // 'inferred-'. But inference persists its results with exactly those ids,
    // so a genogram built by inference had no "manual" rows, re-inferred on
    // EVERY mount, and setRelations() overwrote the human's corrections — which
    // the auto-save effect below then wrote over the database. Editing an
    // inferred relationship and switching tabs silently destroyed the edit.
    if (relations.length > 0) return;
    const inferred = inferRelationshipsFromMembers(members);
    if (inferred.length > 0) {
      console.log(`[FamilyTree] Inferred ${inferred.length} relationships from ${members.length} members — saving to DB`);
      inferredRef.current = true;
      setRelations(inferred);
    }
  }, [members, relations]);

  React.useEffect(() => {
    structureReadyRef.current = false;
    inferredRef.current = false;
    loadStructure();
  }, [loadStructure]);

  React.useEffect(() => {
    if (!structureReadyRef.current || hydratingStructureRef.current) return;
    axios.put(`${backendUrl}/subject/family/structure?root=${encodeURIComponent(root)}`, {
      relationships: relations.map((rel, idx) => ({
        ...rel,
        sortOrder: rel.sortOrder ?? idx,
      })),
      groups: groups.map((group, idx) => ({
        ...group,
        sortOrder: group.sortOrder ?? idx,
      })),
    }).catch(() => {});
  }, [backendUrl, groups, relations, root]);

  React.useEffect(() => {
    if (!members.length) {
      setSelectedId(null);
      return;
    }
    if (!members.some(m => String(m.id) === String(selectedId))) {
      setSelectedId(String(members[0].id));
    }
  }, [members, selectedId]);

  React.useLayoutEffect(() => {
    if (activePanel !== 'people' || !peopleListRef.current) return;
    peopleListRef.current.scrollTop = peopleScrollTopRef.current;
  }, [activePanel, editId, selectedId]);

  // Drop references to members that no longer exist.
  //
  // Both updaters return the PREVIOUS array unchanged when nothing needed
  // dropping, so React bails out of the update. This matters: .map() always
  // allocates, so the old version handed back a new identity on every members
  // load even when it changed nothing — which re-ran the auto-save effect below
  // and issued a PUT re-saving identical structure on every load, and gave the
  // chart a new `memberGroupMap` identity, forcing a full rebuild and refit.
  React.useEffect(() => {
    const validIds = new Set(['subject', ...members.map(m => String(m.id))]);
    setRelations(prev => {
      let changed = false;
      const next = [];
      for (const rel of prev) {
        const partnerA = validIds.has(rel.partnerA) ? rel.partnerA : 'subject';
        const partnerB = validIds.has(rel.partnerB) ? rel.partnerB : '';
        const children = (rel.children || []).filter(id => validIds.has(id));
        if (partnerA !== rel.partnerA || partnerB !== rel.partnerB
            || children.length !== (rel.children || []).length) {
          changed = true;
          if (partnerA) next.push({ ...rel, partnerA, partnerB, children });
        } else if (rel.partnerA) {
          next.push(rel);
        } else {
          changed = true;   // dropped: no partnerA
        }
      }
      return changed ? next : prev;
    });
    setGroups(prev => {
      let changed = false;
      const next = prev.map(group => {
        const memberIds = (group.memberIds || []).filter(id => validIds.has(id));
        if (memberIds.length !== (group.memberIds || []).length) {
          changed = true;
          return { ...group, memberIds };
        }
        return group;
      });
      return changed ? next : prev;
    });
  }, [members]);

  const MEMBER_FIELDS = ['name', 'name_phonetic', 'relation', 'sex', 'birth_year', 'death_year', 'birth_place', 'death_place', 'married_name', 'fate', 'fate_detail', 'notes', 'source_locator'];
  const toPayload = (d) => Object.fromEntries(MEMBER_FIELDS.map(k => [k, d[k] ?? '']));

  const personOptions = React.useMemo(() => {
    const hasSubjectMember = members.some(m => m.relation === 'subject');
    const mapped = members.map(m => ({
      ...m,
      id: String(m.id),
      // Override name for the subject member to use the display name
      ...(m.relation === 'subject' ? { name: subjectName || m.name } : {}),
    }));
    // Only add synthetic subject if no member with relation='subject' exists
    if (!hasSubjectMember) {
      mapped.unshift({ id: 'subject', name: subjectName, relation: 'subject', birth_year: subjectRecord?.born || '', death_year: subjectRecord?.death_year || '', fate: 'unknown' });
    }
    // Alphabetical by name, keeping the subject entry pinned first
    mapped.sort((a, b) => {
      if (a.relation === 'subject' && b.relation !== 'subject') return -1;
      if (b.relation === 'subject' && a.relation !== 'subject') return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return mapped;
  }, [members, subjectName, subjectRecord]);

  const peopleById = React.useMemo(
    () => Object.fromEntries(personOptions.map(person => [String(person.id), person])),
    [personOptions]
  );

  const chartData = React.useMemo(
    () => membersToChartData(members, relations, subjectRecord, subjectName),
    [members, relations, subjectRecord, subjectName]
  );

  // ── Duplicate detection ──
  // Two members are "duplicate" if their normalized names match
  const duplicateIds = React.useMemo(() => {
    const ids = new Set();
    if (members.length < 2) return ids;
    // Normalize: lowercase, trim, collapse whitespace, strip diacritics
    const normalize = (name) => (name || '').toLowerCase().trim()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
    const seen = {};  // normalized name → [member ids]
    members.forEach(m => {
      if (m.relation === 'subject') return;
      const key = normalize(m.name);
      if (!key) return;
      if (!seen[key]) seen[key] = [];
      seen[key].push(m.id);
    });
    Object.values(seen).forEach(group => {
      if (group.length > 1) group.forEach(id => ids.add(id));
    });
    return ids;
  }, [members]);

  const [showDuplicatesOnly, setShowDuplicatesOnly] = React.useState(false);
  const [detailedDuplicates, setDetailedDuplicates] = React.useState(false);
  const [mergeSelection, setMergeSelection] = React.useState(new Set()); // set of member IDs selected for merge
  const [searchQuery, setSearchQuery] = React.useState('');

  // Shared by the merge checkbox's click and keyboard handlers.
  const toggleMergeSelection = React.useCallback((memberId) => {
    setMergeSelection(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }, []);

  // Normalize a name for duplicate grouping and sorting
  const normalizeName = React.useCallback((name) => (name || '').toLowerCase().trim()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' '), []);

  // Sort helper: by last name, then first name
  const sortName = React.useCallback((name) => {
    const parts = (name || '').trim().split(/\s+/);
    if (parts.length <= 1) return [parts[0] || '', ''];
    const last = parts[parts.length - 1];
    const first = parts.slice(0, -1).join(' ');
    return [last.toLowerCase(), first.toLowerCase()];
  }, []);

  // Primary sort: birth year ascending (oldest first). No year → end. Tiebreak: name.
  const ageThenNameSorter = React.useCallback((a, b) => {
    const aYear = parseInt(a.birth_year, 10) || 0;
    const bYear = parseInt(b.birth_year, 10) || 0;
    if (aYear && bYear) return aYear - bYear;      // both have years → oldest first
    if (aYear && !bYear) return -1;                 // a has year, b doesn't → a first
    if (!aYear && bYear) return 1;                  // b has year, a doesn't → b first
    // Neither has a year → sort by name
    const [aLast, aFirst] = sortName(a.name);
    const [bLast, bFirst] = sortName(b.name);
    return aLast.localeCompare(bLast) || aFirst.localeCompare(bFirst);
  }, [sortName]);

  // Fuzzy search: match if all query tokens appear (in any order) in the searchable text
  const fuzzyMatch = React.useCallback((member, query) => {
    if (!query) return true;
    const haystack = normalizeName(
      [member.name, member.relation, member.birth_year, member.death_year, member.birth_place, member.fate].join(' ')
    );
    const tokens = normalizeName(query).split(/\s+/).filter(Boolean);
    return tokens.every(tok => haystack.includes(tok));
  }, [normalizeName]);

  const groupedMembers = React.useMemo(() => {
    const relationOrder = ['Parents', 'Spouse', 'Siblings', 'Children', 'Grandchildren', 'Extended'];

    if (showDuplicatesOnly) {
      // ── Duplicate view: group by normalized name so duplicates sit side-by-side ──
      const byName = {};
      members.forEach(member => {
        if (member.relation === 'subject') return;
        if (!duplicateIds.has(member.id)) return;
        if (!fuzzyMatch(member, searchQuery)) return;
        const key = normalizeName(member.name);
        if (!byName[key]) byName[key] = [];
        byName[key].push(member);
      });
      return Object.entries(byName)
        .sort(([a], [b]) => {
          const [aL, aF] = sortName(a);
          const [bL, bF] = sortName(b);
          return aL.localeCompare(bL) || aF.localeCompare(bF);
        })
        .map(([, items]) => {
          const label = items[0].name;
          items.sort((a, b) => {
            const relA = RELATION_CONFIG[a.relation]?.label || a.relation;
            const relB = RELATION_CONFIG[b.relation]?.label || b.relation;
            return relA.localeCompare(relB) || (a.source || '').localeCompare(b.source || '');
          });
          return { group: `${label} (${items.length}×)`, items };
        });
    }

    // ── Normal view: group by relation type, sorted by age within ──
    const grouped = {};
    members.forEach(member => {
      if (member.relation === 'subject') return;
      if (!fuzzyMatch(member, searchQuery)) return;
      const bucket = RELATION_CONFIG[member.relation]?.group || 'Other';
      if (!grouped[bucket]) grouped[bucket] = [];
      grouped[bucket].push(member);
    });
    Object.values(grouped).forEach(items => items.sort(ageThenNameSorter));
    return relationOrder.filter(key => grouped[key]).map(key => ({ group: key, items: grouped[key] }));
  }, [members, duplicateIds, showDuplicatesOnly, searchQuery, fuzzyMatch, normalizeName, sortName, ageThenNameSorter]);

  // IDs of members already wired into at least one relationship (as partner or child)
  const connectedIds = React.useMemo(() => {
    const ids = new Set();
    relations.forEach(r => {
      if (r.partnerA) ids.add(String(r.partnerA));
      if (r.partnerB) ids.add(String(r.partnerB));
      (r.children || []).forEach(c => ids.add(String(c)));
    });
    return ids;
  }, [relations]);

  const selectedMember = React.useMemo(
    () => members.find(m => String(m.id) === String(selectedId)) || null,
    [members, selectedId]
  );

  // Map memberId → [{name, color}] for group badges
  const memberGroupMap = React.useMemo(() => {
    const map = {};
    groups.forEach(g => {
      (g.memberIds || []).forEach(mid => {
        if (!map[mid]) map[mid] = [];
        map[mid].push({ name: g.name, color: g.color || '#6b7280' });
      });
    });
    return map;
  }, [groups]);

  // Group filter for chart
  const [activeGroupFilter, setActiveGroupFilter] = React.useState(null);
  // { chartNodeId: 'great-grandson' } relative to the interviewee. Computed by
  // the chart (it owns the relationship graph) and handed up. Display only —
  // never written back, so a derived value cannot become a stored fact.
  const [kinships, setKinships] = React.useState({});


  const topPanelRef = React.useRef(null);

  const openNew = () => {
    setSelectedId(null);
    setDraft({ ...BLANK_MEMBER });
    setEditId('new');
  };

  const openEdit = (member) => {
    setSelectedId(String(member.id));
    setDraft({ ...BLANK_MEMBER, ...member });
    setEditId(member.id);
  };

  const cancel = () => {
    setEditId(null);
    setDraft(BLANK_MEMBER);
  };

  const save = () => {
    setSaving(true);
    const isNew = editId === 'new';
    const payload = toPayload(draft);
    console.log('[FamilyTree] save', isNew ? 'POST' : 'PUT', editId, payload);
    const req = isNew
      ? axios.post(`${backendUrl}/subject/family?root=${encodeURIComponent(root)}`, payload)
      : axios.put(`${backendUrl}/subject/family/${editId}?root=${encodeURIComponent(root)}`, payload);
    req
      .then(() => { console.log('[FamilyTree] save OK'); load(); cancel(); })
      .catch(err => { console.error('[FamilyTree] save FAILED', err.response?.status, err.response?.data || err.message); pushToast('family-member-save', 'error', 'Save failed: ' + (err.response?.data?.detail || err.message)); })
      .finally(() => setSaving(false));
  };

  const remove = async (id) => {
    if (!await confirm({ message: 'Remove this family member?', title: 'Remove family member?', confirmLabel: 'Remove', destructive: true })) return;
    axios.delete(`${backendUrl}/subject/family/${id}?root=${encodeURIComponent(root)}`).then(load);
  };

  const dField = (key, label, placeholder = '', type = 'input', opts = null) => (
    <div style={{ marginBottom: '8px', minWidth: 0, boxSizing: 'border-box' }}>
      <label style={{ display: 'block', fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '2px' }}>{label}</label>
      {type === 'select' ? (
        <Select
          value={draft[key]}
          onChange={e => setDraft(prev => ({ ...prev, [key]: e.target.value }))}
          theme={theme}
        >
          {opts.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </Select>
      ) : type === 'autocomplete' ? (
        <AutocompleteInput
          value={draft[key] ?? ''}
          onChange={e => setDraft(prev => ({ ...prev, [key]: e.target.value }))}
          endpoint={opts?.endpoint || '/autocomplete/places'}
          backendUrl={backendUrl}
          placeholder={placeholder}
          theme={theme}
          extraParams={opts?.extraParams || {}}
          minChars={opts?.minChars || 2}
          style={{ width: '100%', padding: '6px 8px', background: theme.inputBg || theme.card, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '0.8rem', outline: 'none', boxSizing: 'border-box' }}
        />
      ) : type === 'textarea' ? (
        <ClearableField
          as="textarea"
          theme={theme}
          value={draft[key]}
          onChange={e => setDraft(prev => ({ ...prev, [key]: e.target.value }))}
          onClear={() => setDraft(prev => ({ ...prev, [key]: '' }))}
          rows={6}
          placeholder={placeholder}
          style={{ width: '100%', padding: '6px 8px', background: theme.inputBg || theme.card, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '0.8rem', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
        />
      ) : (
        <ClearableField
          theme={theme}
          value={draft[key] ?? ''}
          onChange={e => setDraft(prev => ({ ...prev, [key]: e.target.value }))}
          onClear={() => setDraft(prev => ({ ...prev, [key]: '' }))}
          placeholder={placeholder}
          style={{ width: '100%', padding: '6px 8px', background: theme.inputBg || theme.card, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '0.8rem', outline: 'none', boxSizing: 'border-box' }}
        />
      )}
    </div>
  );

  const exportGEDCOM = () => {
    setExporting(true);
    const gedcom = generateGEDCOM(subjectName, subjectRecord, members, { relationships: relations, groups });
    const blob = new Blob([gedcom], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(subjectName || 'genogram').replace(/[^a-z0-9]/gi, '_')}_genogram_${_ts()}.ged`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setExporting(false);
  };

  // ── Admin: Clear all genogram data (auto-downloads JSON backup first) ──
  const handleClearAll = async () => {
    setClearing(true);
    try {
      // Step 1: download full JSON backup
      const res = await axios.get(`${backendUrl}/subject/family/backup?root=${encodeURIComponent(root)}`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `genogram_backup_${(subjectName || root).replace(/[^a-z0-9]/gi, '_')}_${_ts()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // Step 2: delete everything
      await axios.delete(`${backendUrl}/subject/family/clear-all?root=${encodeURIComponent(root)}`);
      // Step 3: refresh UI
      setMembers([]);
      setRelations([]);
      setGroups([]);
      setSelectedId(null);
      setEditId(null);
      if (onMembersLoaded) onMembersLoaded(0);
    } catch (e) {
      pushToast('family-clear-all', 'error', 'Clear failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setClearing(false);
      setShowClearConfirm(false);
    }
  };

  // ── Admin: Restore from JSON backup file ──
  const handleRestoreJson = async (file) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await axios.post(
        `${backendUrl}/subject/family/restore?root=${encodeURIComponent(root)}`,
        data,
      );
      // Reload
      const [mRes, sRes] = await Promise.all([
        axios.get(`${backendUrl}/subject/family?root=${encodeURIComponent(root)}`),
        axios.get(`${backendUrl}/subject/family/structure?root=${encodeURIComponent(root)}`),
      ]);
      setMembers(mRes.data || []);
      setRelations(sRes.data.relationships || []);
      setGroups(sRes.data.groups || []);
      if (onMembersLoaded) onMembersLoaded((mRes.data || []).length);
      pushToast('family-restore', 'success', `Restored ${data.members?.length || 0} members, ${data.relationships?.length || 0} relationships, ${data.groups?.length || 0} groups.`);
    } catch (e) {
      pushToast('family-restore', 'error', 'Restore failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const addRelationship = () => {
    if (!relationshipDraft.partnerA || (relationshipDraft.partnerB && relationshipDraft.partnerA === relationshipDraft.partnerB)) return;
    setRelations(prev => prev.concat({
      id: `rel_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      partnerA: relationshipDraft.partnerA,
      partnerB: relationshipDraft.partnerB,
      children: relationshipDraft.children,
      label: relationshipDraft.label.trim(),
      source: 'manual',
      sortOrder: prev.length,
    }));
    setRelationshipDraft({ partnerA: subjectRef, partnerB: '', children: [], label: '' });
  };

  const toggleRelationshipChild = (id) => {
    setRelationshipDraft(prev => ({
      ...prev,
      children: prev.children.includes(id)
        ? prev.children.filter(childId => childId !== id)
        : prev.children.concat(id),
    }));
  };

  const GROUP_COLORS = ['#2563eb', '#dc2626', '#16a34a', '#9333ea', '#ea580c', '#0891b2', '#be185d', '#854d0e'];
  const createGroup = () => {
    if (!groupName.trim()) return;
    setGroups(prev => prev.concat({
      id: `grp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: groupName.trim(),
      memberIds: [],
      sortOrder: prev.length,
      color: GROUP_COLORS[prev.length % GROUP_COLORS.length],
    }));
    setGroupName('');
  };

  const assignToGroup = (groupId, memberId) => {
    setGroups(prev => prev.map(group => ({
      ...group,
      memberIds: group.id === groupId
        ? Array.from(new Set([...(group.memberIds || []), memberId]))
        : (group.memberIds || []).filter(id => id !== memberId),
    })));
  };

  const removeFromGroup = (groupId, memberId) => {
    setGroups(prev => prev.map(group => group.id === groupId
      ? { ...group, memberIds: (group.memberIds || []).filter(id => id !== memberId) }
      : group));
  };

  const moveRelationship = (id, direction) => {
    setRelations(prev => {
      const idx = prev.findIndex(rel => rel.id === id);
      if (idx < 0) return prev;
      const nextIdx = idx + direction;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
      return next.map((rel, order) => ({ ...rel, sortOrder: order }));
    });
  };

  const ungroupedMembers = React.useMemo(() => {
    const groupedIds = new Set(groups.flatMap(group => group.memberIds || []));
    return personOptions.filter(member => !groupedIds.has(String(member.id)));
  }, [groups, personOptions]);

  const renderPersonEditor = () => (
    <div style={{ boxSizing: 'border-box', display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '14px', padding: '12px 14px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexShrink: 0 }}>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: theme.glossaryAccent, letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Edit3 size={12} strokeWidth={2} />
          {editId === 'new' ? 'ADD PERSON' : 'EDIT PERSON'}
        </div>
        <Button variant="ghost" size="sm" icon={X} onClick={cancel} theme={theme} />
      </div>

      <div style={{ width: '100%', boxSizing: 'border-box', overflowY: 'auto', minHeight: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px', minWidth: 0, boxSizing: 'border-box' }}>
          <div style={{ gridColumn: '1 / -1' }}>{dField('name', 'Full Name', 'e.g. Miriam Szpigler')}</div>
          <div style={{ gridColumn: '1 / -1' }}>{dField('name_phonetic', 'Pronunciation', 'e.g. meer-ee-AM SHPIG-ler')}</div>
          {dField('relation', 'Relation', '', 'select', Object.entries(RELATION_CONFIG).map(([value, cfg]) => ({ value, label: cfg.label })))}
          {dField('sex', 'Sex', '', 'select', [{ value: '', label: '—' }, { value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }])}
          {dField('fate', 'Fate', '', 'select', Object.entries(FATE_CONFIG).map(([value, cfg]) => ({ value, label: cfg.label })))}
          {dField('married_name', 'Married Name', 'e.g. Rosenblom')}
          <HistoricalDatePicker label="Born" value={draft.birth_year} onChange={v => setDraft(d => ({ ...d, birth_year: v }))} theme={theme} />
          <HistoricalDatePicker label="Died" value={draft.death_year} onChange={v => setDraft(d => ({ ...d, death_year: v }))} theme={theme} />
          {dField('birth_place', 'Birth Place', 'e.g. Bedzin, Poland', 'autocomplete', { endpoint: '/autocomplete/places' })}
          {dField('death_place', 'Death Place', 'e.g. Stockholm', 'autocomplete', { endpoint: '/autocomplete/places' })}
          <div style={{ gridColumn: '1 / -1' }}>{dField('fate_detail', 'Fate Detail', 'e.g. Auschwitz, August 1943', 'autocomplete', { endpoint: '/autocomplete/places', minChars: 3 })}</div>
          <div style={{ gridColumn: '1 / -1' }}>{dField('notes', 'Notes', 'Additional context...', 'textarea')}</div>
          <div style={{ gridColumn: '1 / -1' }}>{dField('source_locator', 'Source', 'e.g. Tape 2 00:12:34')}</div>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
          <Button
            variant="primary"
            icon={Save}
            onClick={save}
            disabled={saving || !draft.name.trim()}
            theme={theme}
            style={{ flex: 1 }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="outline" icon={X} onClick={cancel} theme={theme}>Cancel</Button>
        </div>

        {editId !== 'new' && (
          <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${theme.border}` }}>
            <Button
              variant="danger-ghost"
              size="sm"
              icon={Trash2}
              onClick={async () => { await remove(editId); cancel(); }}
              theme={theme}
            >
              Delete Person
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const renderPersonDetailsCard = (member) => {
    const relCfg = RELATION_CONFIG[member.relation] || RELATION_CONFIG.other;
    // Relationship to the interviewee, computed from the graph. Chart nodes key
    // on the member id, except the subject, which uses the 'subject' sentinel.
    const kinship = member.relation === 'subject'
      ? null
      : (kinships[String(member.id)] || kinships[member.id] || null);
    const fateCfg = FATE_CONFIG[member.fate] || FATE_CONFIG.unknown;
    const isEditing = String(editId) === String(member.id);
    if (isEditing) {
      return (
        <div key={`person-edit-${member.id}`} style={{ marginBottom: '12px' }}>
          {renderPersonEditor()}
        </div>
      );
    }
    return (
      <div
        key={`person-card-${member.id}`}
        onClick={() => setSelectedId(String(member.id))}
        role="button"
        tabIndex={0}
        aria-label={`Select ${member.name}`}
        onKeyDown={e => {
          // Only the card itself: Enter/Space on a nested Edit/Delete button
          // bubbles up here too.
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedId(String(member.id)); }
        }}
        style={{
          background: theme.card,
          borderTop: `1px solid ${theme.border}`,
          borderRight: `1px solid ${theme.border}`,
          borderBottom: `1px solid ${theme.border}`,
          borderLeft: `3px solid ${relCfg.color}`,
          borderRadius: '10px',
          padding: '12px 14px',
          marginBottom: '10px',
          fontSize: theme.fsCardText,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: `calc(${theme.fsCardText} + 2px)` }}>{member.name}</span>
              <span style={{ fontSize: '0.62rem', color: relCfg.color, fontWeight: 700, letterSpacing: '0.04em' }}>{relCfg.label.toUpperCase()}</span>
              <span style={{ fontSize: '0.62rem', fontWeight: 700, color: fateCfg.color, background: fateCfg.bg, padding: '1px 7px', borderRadius: '10px' }}>{fateCfg.label}</span>
              {/* Why this person is in the archive at all: their relationship to
                  the interviewee. Derived from the relationship graph, so it is
                  labelled as worked out rather than recorded, and it is shown
                  only here — never on the chart cards, which stay as entered. */}
              {kinship && (
                <span
                  title={`Worked out from the family relationships, not recorded on this person. ${subjectName || 'The interviewee'}'s ${kinship}.`}
                  style={{
                    fontSize: '0.62rem', fontWeight: 600, color: theme.textSub,
                    background: theme.bg, border: `1px dashed ${theme.border}`,
                    padding: '1px 7px', borderRadius: '10px', whiteSpace: 'nowrap',
                  }}
                >
                  {subjectName ? `${subjectName.split(' ')[0]}'s ` : ''}{kinship}
                </span>
              )}
              <span style={{ fontSize: '0.55rem', fontWeight: 700, padding: '1px 6px', borderRadius: '10px', opacity: 0.7,
                color: member.source === 'manual' ? '#007aff' : member.source === 'gedcom' ? '#0f766e' : theme.textSub,
                background: member.source === 'manual' ? '#007aff14' : member.source === 'gedcom' ? '#0f766e14' : `${theme.border}44`,
                border: `1px solid ${member.source === 'manual' ? '#007aff33' : member.source === 'gedcom' ? '#0f766e33' : theme.border}`,
              }}>
                {member.source === 'manual' ? 'MANUAL' : member.source === 'gedcom' ? 'GEDCOM' : 'AI'}
              </span>
            </div>
            {member.name_phonetic && (
              <div style={{ fontSize: '0.72rem', color: theme.textSub, marginTop: '4px', fontStyle: 'italic' }}>
                {member.name_phonetic}
              </div>
            )}
            {(member.birth_year || member.death_year || member.birth_place) && (
              <div style={{ fontSize: '0.72rem', color: theme.textSub, marginTop: '5px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {(member.birth_year || member.death_year) && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Clock size={10} strokeWidth={2} />
                    {member.birth_year || '?'}{member.death_year ? ` - ${member.death_year}` : ''}
                  </span>
                )}
                {member.birth_place && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MapPin size={10} strokeWidth={2} />
                    {member.birth_place}
                  </span>
                )}
              </div>
            )}
            {member.fate_detail && (
              <div style={{ marginTop: '8px', color: theme.text, lineHeight: 1.55 }}>
                {member.fate_detail}
              </div>
            )}
            {member.notes && (
              <div style={{ marginTop: '8px', color: theme.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {member.notes}
              </div>
            )}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
              <button onClick={e => { e.stopPropagation(); openEdit(member); }} style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '5px', padding: '4px 6px', cursor: 'pointer', color: theme.textSub, display: 'flex', alignItems: 'center' }}>
                <Edit3 size={11} strokeWidth={2} />
              </button>
              <button onClick={e => { e.stopPropagation(); remove(member.id); }} style={{ background: 'none', border: '1px solid #ef444444', borderRadius: '5px', padding: '3px 6px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.6rem', fontWeight: 600 }}>
                <Trash2 size={10} strokeWidth={2} />Delete
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const mergeDuplicates = async (keepId, mergeIds) => {
    if (!await confirm({
      message: `Merge ${mergeIds.length} duplicate(s) into this record? The others will be deleted and their data combined.`,
      title: 'Merge duplicates?',
      confirmLabel: 'Merge',
      destructive: true,
    })) return;
    axios.post(`${backendUrl}/subject/family/merge-members?root=${encodeURIComponent(root)}`, { keep_id: keepId, merge_ids: mergeIds })
      .then(r => {
        const d = r.data;
        pushToast('family-merge-members', 'success', `Merged ${d.merged} member(s) into "${members.find(m => m.id === keepId)?.name || keepId}". Fields updated: ${d.fields_updated?.join(', ') || 'none'}`);
        load();
        loadStructure();
      })
      .catch(err => pushToast('family-merge-members', 'error', 'Merge failed: ' + (err.response?.data?.detail || err.message)));
  };

  // Auto-exit duplicate view when no duplicates remain
  React.useEffect(() => {
    if (showDuplicatesOnly && duplicateIds.size === 0) {
      setShowDuplicatesOnly(false);
      setDetailedDuplicates(false);
    }
  }, [showDuplicatesOnly, duplicateIds.size]);

  const renderCompactCard = (member, { duplicateGroup } = {}) => {
    const relCfg = RELATION_CONFIG[member.relation] || RELATION_CONFIG.other;
    const fateCfg = FATE_CONFIG[member.fate] || FATE_CONFIG.unknown;
    const isSelected = String(selectedId) === String(member.id);
    const isDuplicate = duplicateIds.has(member.id);
    return (
      <div
        key={`compact-${member.id}`}
        data-member-id={member.id}
        onClick={() => setSelectedId(String(member.id))}
        role="button"
        tabIndex={0}
        aria-label={`Select ${member.name}`}
        aria-pressed={isSelected}
        onKeyDown={e => {
          // Only the card itself: the merge checkbox and the row's action
          // buttons bubble their key events up here.
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedId(String(member.id)); }
        }}
        style={{
          background: isSelected ? `${theme.glossaryAccent}14` : isDuplicate ? '#ef444408' : theme.card,
          borderLeft: `3px solid ${isDuplicate ? '#ef4444' : relCfg.color}`,
          borderTop: `1px solid ${isSelected ? theme.glossaryAccent : isDuplicate ? '#ef444433' : theme.border}`,
          borderRight: `1px solid ${isSelected ? theme.glossaryAccent : isDuplicate ? '#ef444433' : theme.border}`,
          borderBottom: `1px solid ${isSelected ? theme.glossaryAccent : isDuplicate ? '#ef444433' : theme.border}`,
          borderRadius: '8px',
          padding: '7px 10px',
          marginBottom: '4px',
          cursor: 'pointer',
          fontSize: '0.72rem',
          transition: 'background 0.15s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
          {/* Merge select checkbox */}
          {canEdit && (
            <div
              onClick={e => {
                e.stopPropagation();
                toggleMergeSelection(member.id);
              }}
              role="checkbox"
              aria-checked={mergeSelection.has(member.id)}
              aria-label={`Select ${member.name} for merge`}
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleMergeSelection(member.id);
                }
              }}
              title="Select for merge"
              style={{
                width: '16px', height: '16px', flexShrink: 0, borderRadius: '4px', cursor: 'pointer',
                border: `1.5px solid ${mergeSelection.has(member.id) ? '#16a34a' : theme.border}`,
                background: mergeSelection.has(member.id) ? '#16a34a' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {mergeSelection.has(member.id) && <Check size={10} strokeWidth={3} color="#fff" />}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: '0.76rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.name}</span>
              {member.sex && (() => { const gc = genderColors[member.sex]; return gc ? (
                <span style={{ fontSize: '0.48rem', fontWeight: 800, color: gc, background: `${gc}18`, border: `1px solid ${gc}44`, padding: '1px 5px', borderRadius: '999px', lineHeight: 1.3 }}>{member.sex}</span>
              ) : null; })()}
              <span style={{ fontSize: '0.5rem', fontWeight: 700, color: fateCfg.color, background: fateCfg.bg, padding: '1px 5px', borderRadius: '10px' }}>{fateCfg.label}</span>
              <span style={{ fontSize: '0.48rem', fontWeight: 700, padding: '1px 5px', borderRadius: '10px', opacity: 0.7,
                color: member.source === 'manual' ? '#007aff' : member.source === 'gedcom' ? '#0f766e' : theme.textSub,
                background: member.source === 'manual' ? '#007aff14' : member.source === 'gedcom' ? '#0f766e14' : `${theme.border}44`,
                border: `1px solid ${member.source === 'manual' ? '#007aff33' : member.source === 'gedcom' ? '#0f766e33' : theme.border}`,
              }}>
                {member.source === 'manual' ? 'MANUAL' : member.source === 'gedcom' ? 'GEDCOM' : 'AI'}
              </span>
              {isDuplicate && (
                <span style={{ fontSize: '0.48rem', fontWeight: 700, padding: '1px 5px', borderRadius: '10px',
                  color: '#ef4444', background: '#ef444418', border: '1px solid #ef444433',
                }}>
                  DUPLICATE
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
              <span style={{ fontSize: '0.6rem', color: theme.textSub }}>
                {member.birth_year || '?'}{member.death_year ? ` – ${member.death_year}` : ''}{member.birth_place ? ` · ${member.birth_place}` : ''}
              </span>
              {member.source_locator && <PlayButton sourceLocator={member.source_locator} theme={theme} size="small" />}
            </div>
            {(memberGroupMap[String(member.id)] || []).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '3px' }}>
                {memberGroupMap[String(member.id)].map((g, i) => (
                  <span key={i} style={{ fontSize: '0.44rem', fontWeight: 700, color: g.color, background: `${g.color}14`, border: `1px solid ${g.color}33`, padding: '0px 4px', borderRadius: '6px' }}>
                    {g.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
              {duplicateGroup && duplicateGroup.length > 1 && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    const others = duplicateGroup.filter(m => m.id !== member.id).map(m => m.id);
                    mergeDuplicates(member.id, others);
                  }}
                  title="Keep this record and merge the other duplicate(s) into it"
                  style={{ background: '#16a34a14', border: '1px solid #16a34a44', borderRadius: '4px', padding: '3px 6px', cursor: 'pointer', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.52rem', fontWeight: 700 }}
                >
                  <Check size={9} strokeWidth={2.5} /> Keep
                </button>
              )}
              {isSelected && (
                <button onClick={e => { e.stopPropagation(); openEdit(member); }} style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '4px', padding: '3px 5px', cursor: 'pointer', color: theme.textSub, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <Edit3 size={10} strokeWidth={2} />
                </button>
              )}
            </div>
          )}
        </div>
        {/* Detailed view — shows all fields; highlights diffs when in duplicate group */}
        {detailedDuplicates && (() => {
          const others = (duplicateGroup && duplicateGroup.length > 1) ? duplicateGroup.filter(m => m.id !== member.id) : [];
          const hasDiffComparison = others.length > 0;
          const fields = [
            { key: 'name', label: 'Name' },
            { key: 'relation', label: 'Relation' },
            { key: 'sex', label: 'Sex' },
            { key: 'birth_year', label: 'Born' },
            { key: 'death_year', label: 'Died' },
            { key: 'birth_place', label: 'Birth place' },
            { key: 'death_place', label: 'Death place' },
            { key: 'married_name', label: 'Married name' },
            { key: 'fate', label: 'Fate' },
            { key: 'fate_detail', label: 'Fate detail' },
            { key: 'notes', label: 'Notes' },
            { key: 'source', label: 'Source', format: v => v.toUpperCase() },
            { key: 'source_locator', label: 'Source ref' },
          ];
          return (
            <div style={{ marginTop: '6px', padding: '6px 8px', background: `${theme.border}11`, borderRadius: '6px', fontSize: '0.58rem' }}>
              {fields.map(({ key, label, format }) => {
                const rawVal = (member[key] || '').toString().trim();
                const myVal = format ? format(rawVal) : rawVal;
                if (!hasDiffComparison) {
                  // Normal detailed view — just show all fields, skip empty
                  if (!rawVal) return null;
                  return (
                    <div key={key} style={{ display: 'flex', gap: '6px', padding: '1px 0', borderBottom: `1px solid ${theme.border}22`, alignItems: 'center' }}>
                      <span style={{ width: '70px', flexShrink: 0, color: theme.textSub, fontWeight: 600 }}>{label}</span>
                      <span style={{ flex: 1, color: theme.text }}>{myVal}</span>
                      {key === 'source_locator' && rawVal && <PlayButton sourceLocator={rawVal} theme={theme} size="small" />}
                    </div>
                  );
                }
                // Duplicate comparison — highlight differences
                const otherVals = others.map(o => { const rv = (o[key] || '').toString().trim(); return format ? format(rv) : rv; });
                const allSame = otherVals.every(v => v === myVal);
                const rawOtherVals = others.map(o => (o[key] || '').toString().trim());
                if (!rawVal && rawOtherVals.every(v => !v)) return null;
                const isDiff = !allSame;
                const hasValue = !!myVal;
                const othersHaveValue = otherVals.some(v => !!v);
                const highlight = isDiff ? (hasValue && !othersHaveValue ? '#16a34a' : !hasValue && othersHaveValue ? '#ef4444' : '#f59e0b') : null;
                return (
                  <div key={key} style={{ display: 'flex', gap: '6px', padding: '1px 0', borderBottom: `1px solid ${theme.border}22` }}>
                    <span style={{ width: '70px', flexShrink: 0, color: theme.textSub, fontWeight: 600 }}>{label}</span>
                    <span style={{
                      flex: 1, color: highlight || theme.text,
                      fontWeight: highlight ? 700 : 400,
                      background: highlight ? `${highlight}10` : 'transparent',
                      padding: highlight ? '0 3px' : 0,
                      borderRadius: '3px',
                    }}>
                      {myVal || '—'}
                      {isDiff && hasValue && !othersHaveValue && <span style={{ marginLeft: '4px', fontSize: '0.46rem', color: '#16a34a' }}>UNIQUE</span>}
                      {isDiff && !hasValue && othersHaveValue && <span style={{ marginLeft: '4px', fontSize: '0.46rem', color: '#ef4444' }}>MISSING</span>}
                      {isDiff && hasValue && othersHaveValue && myVal !== otherVals[0] && <span style={{ marginLeft: '4px', fontSize: '0.46rem', color: '#f59e0b' }}>DIFFERS</span>}
                      {key === 'source_locator' && rawVal && <PlayButton sourceLocator={rawVal} theme={theme} size="small" />}
                    </span>
                  </div>
                );
              }).filter(Boolean)}
            </div>
          );
        })()}
      </div>
    );
  };

  if (loading && members.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '30px', color: theme.textSub, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
        <Loader size={22} strokeWidth={1.5} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ fontSize: '0.8rem' }}>Loading genogram records…</span>
      </div>
    );
  }

  const editPanelOpen = editId !== null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
      {/* Structure failed to load. Shown instead of silently presenting an
          empty genogram, which is indistinguishable from a real one. */}
      {structureError && (
        <div style={{ flexShrink: 0 }}>
          <LoadFailure theme={theme} what="the family structure" onRetry={loadStructure} compact />
        </div>
      )}
      <div ref={containerRef} style={{ display: 'flex', flex: '1 1 auto', minHeight: 0, overflow: 'hidden', boxSizing: 'border-box', width: '100%' }}>
      {/* ── LEFT PANEL: People ── */}
      <div style={{
        flex: `0 0 ${leftPanelWidth}%`, minWidth: '180px', maxWidth: '440px',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '14px',
      }}>
        {/* Header + buttons */}
        <div style={{ padding: '12px 14px 8px', flexShrink: 0 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '8px' }}>
            People ({members.length})
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {canEdit && (
              <button onClick={openNew} style={{ padding: '5px 9px', background: editId === 'new' ? `${theme.glossaryAccent}18` : 'none', color: theme.glossaryAccent, border: `1.5px dashed ${theme.glossaryAccent}`, borderRadius: '7px', fontWeight: 700, fontSize: '0.68rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                <Plus size={11} strokeWidth={2.5} /> Add
              </button>
            )}
            {canEdit && (
              <>
                <input ref={gedcomFileRef} type="file" accept=".ged,.gedcom" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) importGEDCOM(e.target.files[0]); e.target.value = ''; }} />
                <button onClick={() => gedcomFileRef.current?.click()} disabled={importing} title="Import GEDCOM" style={{ padding: '5px 8px', background: importing ? theme.card : '#0f766e14', color: importing ? theme.textSub : '#0f766e', border: `1px solid ${importing ? theme.border : '#0f766e44'}`, borderRadius: '7px', fontWeight: 700, fontSize: '0.68rem', cursor: importing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                  {importing ? <><Loader size={10} style={{ animation: 'spin 1s linear infinite' }} /> …</> : <><Download size={10} strokeWidth={2} /> Import</>}
                </button>
              </>
            )}
            {canEdit && pendingMergeSession && (
              <button
                onClick={() => { setMergeSession(pendingMergeSession); }}
                title="Resume pending GEDCOM merge"
                style={{ padding: '5px 8px', background: '#f59e0b14', color: '#f59e0b', border: '1px solid #f59e0b44', borderRadius: '7px', fontWeight: 700, fontSize: '0.68rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', animation: 'pulse 2s infinite' }}
              >
                <GitMerge size={10} strokeWidth={2} /> Resume Merge
              </button>
            )}
            {members.length > 0 && (
              <button onClick={exportGEDCOM} disabled={exporting} title="Export GEDCOM" style={{ padding: '5px 8px', background: theme.card, color: theme.textSub, border: `1px solid ${theme.border}`, borderRadius: '7px', fontWeight: 700, fontSize: '0.68rem', cursor: exporting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                <Upload size={10} strokeWidth={2} /> Export
              </button>
            )}
            {isAdmin && members.length > 0 && (
              <button onClick={() => setShowClearConfirm(true)} disabled={clearing} title="Clear all genogram data (admin)" style={{ padding: '5px 8px', background: clearing ? theme.card : '#ef444414', color: clearing ? theme.textSub : '#ef4444', border: `1px solid ${clearing ? theme.border : '#ef444444'}`, borderRadius: '7px', fontWeight: 700, fontSize: '0.68rem', cursor: clearing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                {clearing ? <><Loader size={10} style={{ animation: 'spin 1s linear infinite' }} /> …</> : <><Trash2 size={10} strokeWidth={2} /> Clear All</>}
              </button>
            )}
            {isAdmin && (
              <>
                <input ref={jsonRestoreRef} type="file" accept=".json" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) handleRestoreJson(e.target.files[0]); e.target.value = ''; }} />
                <button onClick={() => jsonRestoreRef.current?.click()} title="Restore from JSON backup (admin)" style={{ padding: '5px 8px', background: theme.card, color: '#f59e0b', border: `1px solid #f59e0b44`, borderRadius: '7px', fontWeight: 700, fontSize: '0.68rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                  <Download size={10} strokeWidth={2} /> Restore
                </button>
              </>
            )}
          </div>
          {/* Duplicate filter toggle */}
          {duplicateIds.size > 0 && (
            <button
              onClick={() => setShowDuplicatesOnly(prev => !prev)}
              style={{
                marginTop: '8px',
                padding: '4px 10px',
                background: showDuplicatesOnly ? '#ef444418' : 'none',
                color: '#ef4444',
                border: `1px solid ${showDuplicatesOnly ? '#ef444466' : '#ef444433'}`,
                borderRadius: '7px',
                fontWeight: 700,
                fontSize: '0.62rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              {showDuplicatesOnly ? <X size={10} strokeWidth={2.5} /> : <Users size={10} strokeWidth={2} />}
              {showDuplicatesOnly ? 'Show All' : `${duplicateIds.size} Duplicates`}
            </button>
          )}
          <button
            onClick={() => setDetailedDuplicates(prev => !prev)}
            style={{
              marginTop: '8px', marginLeft: duplicateIds.size > 0 ? '6px' : '0',
              padding: '4px 10px',
              background: detailedDuplicates ? `${theme.glossaryAccent}18` : 'none',
              color: detailedDuplicates ? theme.glossaryAccent : theme.textSub,
              border: `1px solid ${detailedDuplicates ? theme.glossaryAccent : theme.border}`,
              borderRadius: '7px', fontWeight: 700, fontSize: '0.62rem', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: '5px',
            }}
          >
            {detailedDuplicates ? 'Compact' : 'Detailed'}
          </button>
          {/* Merge selection bar */}
          {canEdit && mergeSelection.size >= 2 && (
            <div style={{
              marginTop: '8px', padding: '6px 10px',
              background: '#16a34a14', border: '1px solid #16a34a44', borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
            }}>
              <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#16a34a' }}>
                {mergeSelection.size} selected for merge
              </span>
              <div style={{ display: 'flex', gap: '4px' }}>
                <Button
                  onClick={async () => {
                    const ids = [...mergeSelection];
                    // Show picker: which one to keep?
                    const keepChoices = ids.map(id => members.find(m => m.id === id)).filter(Boolean);
                    const keepId = await prompt({
                      title: 'Which record do you want to keep?',
                      message: 'The others will be merged into it.',
                      choices: keepChoices.map(m => ({ value: m.id, label: `${m.name} (${m.source || 'ai'})` })),
                      confirmLabel: 'Merge',
                    });
                    if (keepId == null) return;
                    const mergeIds = ids.filter(id => id !== keepId);
                    await mergeDuplicates(keepId, mergeIds);
                    setMergeSelection(new Set());
                  }}
                  variant="success"
                  size="sm"
                  icon={GitMerge}
                  theme={theme}
                >
                  Merge
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setMergeSelection(new Set())}
                  theme={theme}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {/* Search filter */}
          {members.length > 5 && (
            <div style={{ position: 'relative', marginTop: '8px' }}>
              <Search size={12} strokeWidth={2} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: theme.textSub, pointerEvents: 'none' }} />
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search names, places, years..."
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '6px 8px 6px 26px',
                  borderRadius: '7px',
                  border: `1px solid ${searchQuery ? theme.glossaryAccent : theme.border}`,
                  background: theme.inputBg || theme.card,
                  color: theme.text,
                  fontSize: '0.7rem',
                  outline: 'none',
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: theme.textSub, padding: '2px', display: 'flex' }}
                >
                  <X size={12} strokeWidth={2} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Scrollable people list */}
        <div ref={peopleListRef} onScroll={e => { peopleScrollTopRef.current = e.currentTarget.scrollTop; }} style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '0 10px 10px', overscrollBehavior: 'contain' }}>
          {/* Subject card — always at top */}
          {subjectRecord && !showDuplicatesOnly && (
            <div style={{ marginBottom: '6px' }}>
              <div style={{ fontSize: '0.52rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.08em', textTransform: 'uppercase', margin: '6px 0 4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ flex: 1, height: '1px', background: theme.glossaryAccent || '#f5a623' }} />
                Subject
                <div style={{ flex: 1, height: '1px', background: theme.glossaryAccent || '#f5a623' }} />
              </div>
              <div
                data-member-id="subject"
                style={{
                  background: theme.card,
                  borderLeft: `3px solid ${theme.glossaryAccent || '#f5a623'}`,
                  border: `1px solid ${theme.glossaryAccent || '#f5a623'}44`,
                  borderLeftWidth: '3px',
                  borderRadius: '8px',
                  padding: '7px 10px',
                  fontSize: '0.72rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: '0.82rem', color: theme.glossaryAccent || '#f5a623' }}>{subjectName}</span>
                  {subjectRecord.sex && (() => { const gc = genderColors[subjectRecord.sex?.toUpperCase()]; return gc ? (
                    <span style={{ fontSize: '0.48rem', fontWeight: 800, color: gc, background: `${gc}18`, border: `1px solid ${gc}44`, padding: '1px 5px', borderRadius: '999px', lineHeight: 1.3 }}>{subjectRecord.sex.toUpperCase()}</span>
                  ) : null; })()}
                  <span style={{ fontSize: '0.5rem', fontWeight: 700, color: FATE_CONFIG.survived.color, background: FATE_CONFIG.survived.bg, padding: '1px 5px', borderRadius: '10px' }}>Survived</span>
                </div>
                <div style={{ fontSize: '0.6rem', color: theme.textSub, marginTop: '2px' }}>
                  {subjectRecord.born || '?'}{subjectRecord.death_year ? ` – ${subjectRecord.death_year}` : ''}
                  {subjectRecord.birthplace ? ` · ${subjectRecord.birthplace}` : ''}
                </div>
                {subjectRecord.nationality && (
                  <div style={{ fontSize: '0.56rem', color: theme.textSub, marginTop: '1px' }}>
                    {[subjectRecord.nationality, subjectRecord.religion, subjectRecord.mother_tongue].filter(Boolean).join(' · ')}
                  </div>
                )}
                {subjectRecord.num_siblings && (
                  <div style={{ fontSize: '0.56rem', color: theme.textSub, marginTop: '1px' }}>Siblings: {subjectRecord.num_siblings}</div>
                )}
                {subjectRecord.father_name && (
                  <div style={{ fontSize: '0.56rem', color: theme.textSub, marginTop: '1px' }}>
                    Father: {subjectRecord.father_name}{subjectRecord.mother_name ? ` · Mother: ${subjectRecord.mother_name}` : ''}
                    {subjectRecord.mother_maiden_name ? ` (née ${subjectRecord.mother_maiden_name})` : ''}
                  </div>
                )}
                {subjectRecord.prewar_address && (
                  <div style={{ fontSize: '0.56rem', color: theme.textSub, marginTop: '1px' }}>Pre-war: {subjectRecord.prewar_address}</div>
                )}
                {subjectRecord.auschwitz_number && (
                  <div style={{ fontSize: '0.56rem', color: theme.textSub, marginTop: '1px' }}>Prisoner #: {subjectRecord.auschwitz_number}</div>
                )}
              </div>
            </div>
          )}
          {groupedMembers.map(({ group, items }) => (
            <div key={`compact-${group}`} style={{ marginBottom: '6px' }}>
              <div style={{ fontSize: '0.52rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.08em', textTransform: 'uppercase', margin: '6px 0 4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ flex: 1, height: '1px', background: theme.border }} />
                {group} ({items.length})
                <div style={{ flex: 1, height: '1px', background: theme.border }} />
              </div>
              {items.map(member => renderCompactCard(member, { duplicateGroup: showDuplicatesOnly ? items : null }))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Draggable divider ── */}
      <div
        onPointerDown={onDividerPointerDown}
        style={{
          flex: '0 0 6px', cursor: 'col-resize',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '3px',
          background: isDraggingRef.current ? `${theme.glossaryAccent}22` : 'transparent',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = `${theme.glossaryAccent}22`; }}
        onMouseLeave={e => { if (!isDraggingRef.current) e.currentTarget.style.background = 'transparent'; }}
      >
        <div style={{ width: '3px', height: '36px', borderRadius: '2px', background: theme.border }} />
      </div>

      {/* ── RIGHT PANEL: Genogram + Relationships + Groups ── */}
      <div ref={rightPanelRef} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Genogram visualization — powered by family-chart */}
        <div ref={topPanelRef} style={{ flex: `0 0 ${topPanelPct}%`, minHeight: '120px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '14px', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '10px 14px 0', flexShrink: 0 }}>
            <div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: theme.textSub }}>Genogram</div>
              <div style={{ fontSize: '0.92rem', fontWeight: 700 }}>{subjectName}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.62rem', color: theme.textSub }}>
                {members.length} person{members.length === 1 ? '' : 's'} · {relations.length} rel{relations.length === 1 ? '' : 's'}
              </span>
              <button
                onClick={() => setShowGenogramHelp(prev => !prev)}
                title="How the genogram works"
                style={{ background: showGenogramHelp ? `${theme.glossaryAccent}18` : 'none', border: `1px solid ${showGenogramHelp ? theme.glossaryAccent : theme.border}`, borderRadius: '6px', padding: '4px', cursor: 'pointer', color: showGenogramHelp ? theme.glossaryAccent : theme.textSub, display: 'flex', alignItems: 'center' }}
              >
                <HelpCircle size={13} strokeWidth={2} />
              </button>
              <button
                onClick={() => setShowCardSettings(prev => !prev)}
                title="Card appearance settings"
                style={{ background: showCardSettings ? `${theme.glossaryAccent}18` : 'none', border: `1px solid ${showCardSettings ? theme.glossaryAccent : theme.border}`, borderRadius: '6px', padding: '4px', cursor: 'pointer', color: showCardSettings ? theme.glossaryAccent : theme.textSub, display: 'flex', alignItems: 'center' }}
              >
                <Settings size={13} strokeWidth={2} />
              </button>
            </div>
          </div>

          {showGenogramHelp && (() => {
            const defaultItems = [
              { title: 'People (Left Panel)', text: 'All family members extracted from the transcript. Click to select, then click the edit icon to modify. Sorted by birth year (oldest first) within each group. Use the search box to filter by name, year, place, or fate.' },
              { title: 'Duplicates & Merging', text: 'Red "DUPLICATE" badges flag members with matching names. Click the duplicate count button to filter and see them side-by-side. Click "Keep" on the best record — the others merge into it (combining data from all sources) and are deleted. Relationships and groups update automatically.' },
              { title: 'Chart (Top Right)', text: 'Interactive family tree powered by the family-chart library. Scroll to zoom, drag to pan. Click a card to center on that person. Only members connected via Relationships appear in the chart. Card width auto-adjusts to fit the longest name.' },
              { title: 'Relationships', text: 'Define couples (Partner A + Partner B) and their children. These connections drive the chart layout. "Rebuild" uses stored AI hints (partner_of, parent_of, sibling_of) from the transcript extraction — no API cost. "AI Map" sends the member list + transcript to Claude for deeper analysis (uses API tokens).' },
              { title: 'GEDCOM Import & Merge', text: 'Import a GEDCOM file from genealogy software (MyHeritage, Ancestry, etc.). If members already exist, a merge screen opens showing side-by-side matches with confidence scores. GEDCOM data becomes the genealogical source of truth; AI data enriches it with testimony context (fate, notes, transcript references). Merge sessions are saved — close and resume later.' },
              { title: 'Groups', text: 'Tag family members with colored labels — e.g. "Deported together", "First marriage", "Emigrated to Israel". Group colors tint chart cards and appear as badges. Use the filter pills above the chart to highlight one group — everyone else dims. Groups are included in report and GEDCOM exports.' },
              { title: 'Unconnected Members', text: 'The amber badge shows how many members are not yet wired into any relationship. These won\'t appear in the chart. Wire them via the relationship editor, Rebuild, or AI Map.' },
              { title: 'Card Settings', text: 'Click the gear icon to customize card appearance — sizes, fonts, colors, and visibility of labels. Changes are saved to your browser and apply instantly.' },
              { title: 'Data Sources', text: 'Members come from three sources: AI (extracted from transcript), GEDCOM (imported from genealogy software), or MANUAL (added by hand). The source badge on each card shows the origin.' },
            ];
            const items = (helpContent && helpContent.items && helpContent.items.length > 0) ? helpContent.items : defaultItems;
            const title = (helpContent && helpContent.title) || 'How the Genogram Works';
            return (
              <div style={{
                position: 'absolute', top: '48px', right: '44px', zIndex: Z.dropdown,
                width: '360px', maxHeight: '70vh', overflowY: 'auto',
                background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)', padding: '16px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: theme.text }}>{title}</span>
                  <button onClick={() => setShowGenogramHelp(false)}
                    style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '6px', padding: '3px', cursor: 'pointer', color: theme.textSub, display: 'flex' }}>
                    <X size={12} strokeWidth={2} />
                  </button>
                </div>
                {items.map(({ title: t, text }) => (
                  <div key={t} style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '0.66rem', fontWeight: 700, color: theme.glossaryAccent, marginBottom: '3px' }}>{t}</div>
                    <div style={{ fontSize: '0.64rem', color: theme.textSub, lineHeight: 1.6 }}>{text}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          {showCardSettings && (
            <GenogramSettingsModal
              theme={theme}
              onClose={() => setShowCardSettings(false)}
            />
          )}

          {/* Group filter pills */}
          {groups.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '4px 14px', flexShrink: 0 }}>
              <button
                onClick={() => setActiveGroupFilter(null)}
                style={{ padding: '2px 8px', fontSize: '0.56rem', fontWeight: 700, borderRadius: '10px', cursor: 'pointer',
                  background: !activeGroupFilter ? `${theme.glossaryAccent}18` : 'transparent',
                  color: !activeGroupFilter ? theme.glossaryAccent : theme.textSub,
                  border: `1px solid ${!activeGroupFilter ? theme.glossaryAccent : theme.border}` }}
              >All</button>
              {groups.map(g => (
                <button
                  key={g.id}
                  onClick={() => setActiveGroupFilter(prev => prev === g.id ? null : g.id)}
                  style={{ padding: '2px 8px', fontSize: '0.56rem', fontWeight: 700, borderRadius: '10px', cursor: 'pointer',
                    background: activeGroupFilter === g.id ? `${g.color || '#6b7280'}22` : 'transparent',
                    color: activeGroupFilter === g.id ? (g.color || '#6b7280') : theme.textSub,
                    border: `1px solid ${activeGroupFilter === g.id ? (g.color || '#6b7280') : theme.border}` }}
                >{g.name} ({(g.memberIds || []).length})</button>
              ))}
            </div>
          )}

          {chartData.length > 0 ? (
            <FamilyChartPane
              data={chartData}
              theme={theme}
              activeGroupFilter={activeGroupFilter}
              onKinships={setKinships}
              onPersonClick={(id) => {
                const strId = String(id);
                // Find the DB member ID (chart may use 'subject' or the actual DB id)
                const datum = chartData.find(d => d.id === strId);
                if (!datum) return;
                const memberId = datum.data._isSubject
                  ? members.find(m => m.relation === 'subject')?.id
                  : strId;
                if (memberId) {
                  setSelectedId(String(memberId));
                  // Scroll the card into view in the left panel
                  setTimeout(() => {
                    const el = peopleListRef.current?.querySelector(`[data-member-id="${memberId}"]`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 50);
                }
              }}
              selectedId={selectedId}
              subjectName={subjectName}
              memberGroupMap={memberGroupMap}
            />
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', color: theme.textSub, textAlign: 'center', padding: '14px' }}>
              <Users size={34} strokeWidth={1.2} style={{ opacity: 0.3 }} />
              <div style={{ fontSize: '0.8rem' }}>No genogram people recorded yet.</div>
              <div style={{ fontSize: '0.72rem', maxWidth: '320px', lineHeight: 1.5 }}>
                Add people in the left panel, then define relationships below. The genogram updates live.
              </div>
            </div>
          )}
        </div>

        {/* ── Horizontal draggable divider ── */}
        <div
          onPointerDown={onVDividerPointerDown}
          style={{
            flex: '0 0 6px', cursor: 'row-resize',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '3px',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = `${theme.glossaryAccent}22`; }}
          onMouseLeave={e => { if (!isDraggingRef.current) e.currentTarget.style.background = 'transparent'; }}
        >
          <div style={{ height: '3px', width: '36px', borderRadius: '2px', background: theme.border }} />
        </div>

        {/* Relationships + Groups side-by-side */}
        <div ref={bottomPanelRef} style={{ flex: 1, minHeight: '80px', display: 'flex', gap: 0, overflow: 'hidden' }}>

        {/* Relationships section */}
        <div style={{ flex: `0 0 ${relGroupSplit}%`, minWidth: '200px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '14px', padding: '14px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', flexShrink: 0, gap: '8px' }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: theme.textSub }}>
              Relationships ({relations.length})
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
              {(() => {
                const unconnectedCount = members.filter(m => m.relation !== 'subject' && !connectedIds.has(String(m.id))).length;
                return unconnectedCount > 0 ? (
                  <span style={{ fontSize: '0.56rem', fontWeight: 700, color: '#f59e0b', background: '#f59e0b14', border: '1px solid #f59e0b33', padding: '2px 7px', borderRadius: '10px' }}>
                    {unconnectedCount} unconnected
                  </span>
                ) : null;
              })()}
              {canEdit && (
                <button
                  onClick={rebuildRelationships}
                  disabled={rebuildingRels || members.length === 0}
                  title="Re-infer relationships from member roles (no AI cost)"
                  style={{ padding: '2px 7px', fontSize: '0.56rem', fontWeight: 700, borderRadius: '10px', cursor: rebuildingRels ? 'not-allowed' : 'pointer',
                    color: '#6366f1', background: '#6366f114', border: '1px solid #6366f133', opacity: rebuildingRels ? 0.5 : 1 }}
                >
                  {rebuildingRels ? '...' : 'Rebuild'}
                </button>
              )}
              {canEdit && (
                <button
                  onClick={runRelationshipQA}
                  disabled={runningRelQA || members.length === 0}
                  title="Ask AI to map all relationships from transcript (uses API tokens)"
                  style={{ padding: '2px 7px', fontSize: '0.56rem', fontWeight: 700, borderRadius: '10px', cursor: runningRelQA ? 'not-allowed' : 'pointer',
                    color: '#0f766e', background: '#0f766e14', border: '1px solid #0f766e33', opacity: runningRelQA ? 0.5 : 1 }}
                >
                  {runningRelQA ? <><Loader size={9} style={{ animation: 'spin 1s linear infinite', display: 'inline' }} /> AI Mapping...</> : 'AI Map'}
                </button>
              )}
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {/* Add relationship form */}
            {canEdit && (
              <div style={{ border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '10px', marginBottom: '10px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  {['partnerA', 'partnerB'].map(field => (
                    <Select
                      key={field}
                      value={relationshipDraft[field]}
                      onChange={e => setRelationshipDraft(prev => ({ ...prev, [field]: e.target.value }))}
                      size="sm"
                      theme={theme}
                    >
                      <option value="">{field === 'partnerA' ? 'Partner A' : 'Partner B'}</option>
                      {personOptions.map(person => <option key={person.id} value={person.id}>{person.name}</option>)}
                    </Select>
                  ))}
                </div>
                <Input
                  value={relationshipDraft.label}
                  onChange={e => setRelationshipDraft(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="Optional label, e.g. married, second marriage"
                  size="sm"
                  theme={theme}
                  style={{ marginTop: '6px' }}
                />
                <div style={{ marginTop: '8px', fontSize: '0.58rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Children
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '6px' }}>
                  {personOptions
                    .filter(person => person.id !== relationshipDraft.partnerA && person.id !== relationshipDraft.partnerB)
                    .filter(person => {
                      const childId = String(person.id);
                      // Always show if already selected in draft; hide if connected elsewhere
                      return relationshipDraft.children.includes(childId) || !connectedIds.has(childId);
                    })
                    .map(person => {
                      const childId = String(person.id);
                      const checked = relationshipDraft.children.includes(childId);
                      return (
                        <button
                          key={person.id}
                          onClick={() => toggleRelationshipChild(childId)}
                          style={{ padding: '4px 7px', borderRadius: '999px', border: `1px solid ${checked ? '#2563eb' : theme.border}`, background: checked ? '#2563eb' : theme.card, color: checked ? '#fff' : theme.textSub, cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700 }}
                        >
                          {person.name}
                        </button>
                      );
                    })}
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  icon={Link2}
                  onClick={addRelationship}
                  theme={theme}
                  style={{ marginTop: '8px' }}
                >
                  Add Relationship
                </Button>
              </div>
            )}

            {/* Existing relationships */}
            {relations.map(rel => (
              <div key={rel.id} style={{ border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '10px', marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span>{(peopleById[rel.partnerA]?.name || 'Unknown')} + {(peopleById[rel.partnerB]?.name || 'Unknown')}</span>
                      {/* Inference guesses at kinship from each person's `relation`
                          string, and those guesses are stored in the same table as
                          relationships taken from the testimony itself. On an archive
                          of record, a reviewer has to be able to tell them apart, so
                          say so rather than leaving the distinction buried in an id. */}
                      {(rel.source === 'inferred' || (!rel.source && String(rel.id).startsWith('inferred-'))) && (
                        <span
                          title="Guessed from each person's stated relation, not recorded from the testimony. Edit to confirm it."
                          style={{
                            fontSize: '0.54rem', fontWeight: 700, letterSpacing: '0.04em',
                            textTransform: 'uppercase', color: '#f59e0b',
                            background: '#f59e0b18', border: '1px solid #f59e0b44',
                            borderRadius: '10px', padding: '1px 7px', whiteSpace: 'nowrap',
                          }}
                        >
                          Guessed
                        </span>
                      )}
                    </div>
                    {rel.label && <div style={{ fontSize: '0.66rem', color: theme.textSub, marginTop: '2px' }}>{rel.label}</div>}
                    <div style={{ fontSize: '0.64rem', color: theme.textSub, marginTop: '4px' }}>
                      Children: {(rel.children || []).map(id => peopleById[id]?.name).filter(Boolean).join(', ') || 'None'}
                    </div>
                  </div>
                  {canEdit && (
                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                      <button
                        onClick={() => {
                          if (editingRelId === rel.id) {
                            setEditingRelId(null);
                          } else {
                            setEditingRelId(rel.id);
                            setRelationshipDraft({
                              partnerA: rel.partnerA,
                              partnerB: rel.partnerB,
                              children: [...(rel.children || [])],
                              label: rel.label || '',
                            });
                          }
                        }}
                        style={{ background: 'none', border: `1px solid ${editingRelId === rel.id ? '#007aff' : theme.border}`, borderRadius: '5px', padding: '3px 5px', cursor: 'pointer', color: editingRelId === rel.id ? '#007aff' : theme.textSub, fontSize: '0.58rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}
                      >
                        <Edit3 size={10} strokeWidth={2} />{editingRelId === rel.id ? 'Cancel' : 'Edit'}
                      </button>
                      <button onClick={() => moveRelationship(rel.id, -1)} style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '5px', padding: '4px 6px', cursor: 'pointer', color: theme.textSub }}>
                        <ChevronUp size={11} strokeWidth={2} />
                      </button>
                      <button onClick={() => moveRelationship(rel.id, 1)} style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '5px', padding: '4px 6px', cursor: 'pointer', color: theme.textSub }}>
                        <ChevronDown size={11} strokeWidth={2} />
                      </button>
                      <button
                        onClick={() => setRelations(prev => prev.filter(item => item.id !== rel.id).map((item, idx) => ({ ...item, sortOrder: idx })))}
                        style={{ background: 'none', border: '1px solid #ef444444', borderRadius: '5px', padding: '3px 5px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '2px', fontSize: '0.58rem', fontWeight: 600 }}
                      >
                        <Trash2 size={10} strokeWidth={2} />Delete
                      </button>
                    </div>
                  )}
                </div>
                {/* Inline relationship editor */}
                {canEdit && editingRelId === rel.id && (
                  <div style={{ marginTop: '8px', padding: '10px', background: theme.sidebar, borderRadius: '8px', border: `1px solid #007aff33` }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '8px' }}>
                      <div>
                        <div style={{ fontSize: '0.58rem', fontWeight: 700, color: theme.textSub, marginBottom: '3px', textTransform: 'uppercase' }}>Partner A</div>
                        <Select value={relationshipDraft.partnerA} onChange={e => setRelationshipDraft(d => ({ ...d, partnerA: e.target.value }))} size="sm" theme={theme}>
                          {personOptions.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                        </Select>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.58rem', fontWeight: 700, color: theme.textSub, marginBottom: '3px', textTransform: 'uppercase' }}>Partner B</div>
                        <Select value={relationshipDraft.partnerB} onChange={e => setRelationshipDraft(d => ({ ...d, partnerB: e.target.value }))} size="sm" theme={theme}>
                          <option value="">Select…</option>
                          {personOptions.filter(p => String(p.id) !== relationshipDraft.partnerA).map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                        </Select>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.58rem', fontWeight: 700, color: theme.textSub, marginBottom: '3px', textTransform: 'uppercase' }}>Children</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                      {personOptions
                        .filter(p => String(p.id) !== relationshipDraft.partnerA && String(p.id) !== relationshipDraft.partnerB)
                        .filter(p => {
                          const pid = String(p.id);
                          // Show if already in this relationship's draft, or if not connected elsewhere
                          return relationshipDraft.children.includes(pid) || !connectedIds.has(pid);
                        })
                        .map(p => {
                        const selected = relationshipDraft.children.includes(String(p.id));
                        return (
                          <button key={p.id} onClick={() => setRelationshipDraft(d => ({
                            ...d,
                            children: selected ? d.children.filter(id => id !== String(p.id)) : [...d.children, String(p.id)]
                          }))}
                            style={{ padding: '2px 7px', borderRadius: '10px', fontSize: '0.65rem', cursor: 'pointer', fontWeight: selected ? 700 : 400,
                              border: `1px solid ${selected ? '#2563eb' : theme.border}`,
                              background: selected ? '#2563eb' : 'transparent',
                              color: selected ? '#fff' : theme.textSub }}>
                            {p.name}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => {
                        // Editing a guess is a person confirming it, so it
                        // stops being flagged as guessed. Without this the
                        // badge would stick forever and there would be no way
                        // to say "I checked this".
                        setRelations(prev => prev.map(r => r.id === rel.id
                          ? { ...r, ...relationshipDraft, source: 'manual' }
                          : r));
                        setEditingRelId(null);
                      }}
                        style={{ padding: '5px 12px', borderRadius: '6px', background: '#007aff', color: '#fff', border: 'none', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                        Save
                      </button>
                      <button onClick={() => setEditingRelId(null)}
                        style={{ padding: '5px 12px', borderRadius: '6px', background: theme.card, color: theme.textSub, border: `1px solid ${theme.border}`, fontSize: '0.72rem', cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Vertical divider between Relationships and Groups ── */}
        <div
          onPointerDown={onRelGroupDividerDown}
          style={{
            flex: '0 0 6px', cursor: 'col-resize',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '3px',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = `${theme.glossaryAccent}22`; }}
          onMouseLeave={e => { if (!isDraggingRef.current) e.currentTarget.style.background = 'transparent'; }}
        >
          <div style={{ width: '3px', height: '36px', borderRadius: '2px', background: theme.border }} />
        </div>

        {/* Groups section */}
        <div style={{ flex: 1, minWidth: '160px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <button
            onClick={() => setGroupsOpen(!groupsOpen)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', color: theme.textSub }}
          >
            <span style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
              Groups ({groups.length})
            </span>
            {groupsOpen ? <ChevronUp size={14} strokeWidth={2} /> : <ChevronDown size={14} strokeWidth={2} />}
          </button>
          {groupsOpen && (
            <div style={{ padding: '0 14px 14px', flex: 1, overflowY: 'auto' }}>
              <div style={{ fontSize: '0.62rem', color: theme.textSub, lineHeight: 1.5, marginBottom: '10px', padding: '6px 8px', background: `${theme.border}22`, borderRadius: '6px' }}>
                Tag family members with colored labels — e.g. "Deported together", "First marriage", "Emigrated to Israel". Group colors tint the chart cards and appear as badges. Use the filter pills above the chart to highlight one group — everyone else dims.
              </div>
              {canEdit && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <Input
                    value={groupName}
                    onChange={e => setGroupName(e.target.value)}
                    placeholder="New group name, e.g. First marriage"
                    size="sm"
                    fullWidth={false}
                    theme={theme}
                    style={{ flex: 1 }}
                  />
                  <Button variant="primary" size="sm" onClick={createGroup} theme={theme}>Add</Button>
                </div>
              )}

              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '0.58rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                  Ungrouped
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {ungroupedMembers.map(member => (
                    <div
                      key={member.id}
                      draggable={canEdit}
                      onDragStart={() => canEdit && setDragMemberId(String(member.id))}
                      onDragEnd={() => setDragMemberId(null)}
                      style={{ padding: '5px 8px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: theme.card, cursor: canEdit ? 'grab' : 'default', fontSize: '0.68rem', fontWeight: 700 }}
                    >
                      {member.name}
                    </div>
                  ))}
                </div>
              </div>

              {groups.map(group => (
                <div
                  key={group.id}
                  onDragOver={e => canEdit && e.preventDefault()}
                  onDrop={() => {
                    if (canEdit && dragMemberId) assignToGroup(group.id, dragMemberId);
                    setDragMemberId(null);
                  }}
                  style={{ border: `1px dashed ${dragMemberId ? theme.glossaryAccent : theme.border}`, borderRadius: '10px', padding: '10px', marginBottom: '8px' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {canEdit ? (
                        <input
                          type="color"
                          value={group.color || '#6b7280'}
                          onChange={e => setGroups(prev => prev.map(g => g.id === group.id ? { ...g, color: e.target.value } : g))}
                          style={{ width: '20px', height: '20px', border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: 'pointer', background: 'none', padding: 0 }}
                          title="Group color"
                        />
                      ) : (
                        <div style={{ width: '20px', height: '20px', borderRadius: '4px', background: group.color || '#6b7280' }} />
                      )}
                      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: group.color || theme.text }}>{group.name}</div>
                    </div>
                    {canEdit && (
                      <button
                        onClick={() => setGroups(prev => prev.filter(item => item.id !== group.id))}
                        style={{ background: 'none', border: '1px solid #ef444444', borderRadius: '5px', padding: '3px 5px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '2px', fontSize: '0.58rem', fontWeight: 600 }}
                      >
                        <Trash2 size={10} strokeWidth={2} />Delete
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {(group.memberIds || []).map(memberId => (
                      canEdit ? (
                        <button
                          key={memberId}
                          onClick={() => removeFromGroup(group.id, memberId)}
                          style={{ padding: '4px 7px', borderRadius: '999px', border: `1px solid ${group.color || theme.border}`, background: `${group.color || theme.glossaryAccent}14`, color: theme.text, cursor: 'pointer', fontSize: '0.66rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          {peopleById[memberId]?.name || memberId} <X size={10} strokeWidth={2} />
                        </button>
                      ) : (
                        <span
                          key={memberId}
                          style={{ padding: '4px 7px', borderRadius: '999px', border: `1px solid ${group.color || theme.border}`, background: `${group.color || theme.glossaryAccent}14`, color: theme.text, fontSize: '0.66rem', display: 'flex', alignItems: 'center' }}
                        >
                          {peopleById[memberId]?.name || memberId}
                        </span>
                      )
                    ))}
                    {(group.memberIds || []).length === 0 && (
                      <div style={{ fontSize: '0.68rem', color: theme.textSub }}>{canEdit ? 'Drop people here to group them.' : 'No members in this group.'}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        </div>{/* close bottom flex row */}
      </div>

      {/* ── MODAL: GEDCOM Merge ── */}
      {(mergeGedcomText || mergeSession) && (
        <GedcomMergeModal
          backendUrl={backendUrl}
          root={root}
          gedcomText={mergeGedcomText}
          existingSession={mergeSession}
          theme={theme}
          onClose={() => { setMergeGedcomText(null); setMergeSession(null); }}
          onComplete={() => {
            setMergeGedcomText(null);
            setMergeSession(null);
            setPendingMergeSession(null);
            load();
            loadStructure();
          }}
        />
      )}

      {/* ── MODAL: Person Editor ── */}
      {canEdit && editId !== null && (
        <div
          onClick={cancel}
          style={{
            position: 'fixed', inset: 0, zIndex: Z.modal,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: '520px', maxHeight: '90vh',
              overflowY: 'auto',
              borderRadius: '16px',
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            }}
          >
            {renderPersonEditor()}
          </div>
        </div>
      )}

      {/* ── MODAL: Clear All Confirmation ── */}
      <Modal
        open={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        showClose={false}
        width={420}
        zIndex={Z.dialog}
        theme={theme}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setShowClearConfirm(false)} theme={theme}>Cancel</Button>
            <Button
              variant="danger"
              loading={clearing}
              disabled={clearing}
              onClick={handleClearAll}
              theme={theme}
            >
              {clearing ? 'Clearing...' : 'Download Backup & Clear All'}
            </Button>
          </>
        )}
      >
        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#ef4444', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Trash2 size={18} strokeWidth={2} /> Clear All Genogram Data
        </div>
        <div style={{ fontSize: '0.82rem', color: theme.text, lineHeight: 1.6, marginBottom: '8px' }}>
          This will permanently delete <strong>all {members.length} members</strong>, {relations.length} relationships, and {groups.length} groups for this subject.
        </div>
        <div style={{ fontSize: '0.78rem', color: theme.textSub, lineHeight: 1.5, marginBottom: '20px', padding: '10px 12px', background: '#f59e0b14', border: '1px solid #f59e0b33', borderRadius: '8px' }}>
          A full JSON backup will be downloaded automatically before deletion. You can restore it later using the <strong>Restore</strong> button.
        </div>
      </Modal>
      </div>
    </div>
  );
}
